import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	FarmData,
	PointMetaData,
	PolygonDrawingStage,
	PolygonValidationStatus,
} from 'interfaces/awd.interface';
import {
	EditablePolygon,
	PointData,
	PolygonBlockedStatus,
	PolygonData,
} from 'interfaces/maps.interface';
import { AREA_CONVERSION_FACTOR } from 'utils/constants';

interface ValidationResponse {
	valid: boolean;
	vldtRs: {
		dsc: string;
	};
}

interface InitialAwdViewState {
	editablePolygons: Record<string, EditablePolygon>;
	isDrawEnabled: boolean;
	isSelectedPolygonBlocked: boolean;
	isSelectedPolygonEditable: boolean;
	isSelectedPolygonSaved: boolean;
	pointsOfFarmId: Record<string, PointMetaData>;
	pointMapTiles: Record<string, PointData>;
	polygonDrawingStage: PolygonDrawingStage;
	polygonMapTiles: Record<string, PolygonData>;
	selectedFarmData: FarmData;
	selectedPoint: undefined | string;
	selectedPolygonIndex: number | undefined;
	selectedPolygonValidationStatus: PolygonValidationStatus;
	selectedPolygonValidationResponse: ValidationResponse[];
}

const initialState: InitialAwdViewState = {
	editablePolygons: {},
	isDrawEnabled: false,
	isSelectedPolygonEditable: false,
	isSelectedPolygonSaved: true,
	isSelectedPolygonBlocked: false,
	pointsOfFarmId: {},
	pointMapTiles: {},
	polygonDrawingStage: 'PRE_START',
	polygonMapTiles: {},
	selectedFarmData: {
		farmId: '',
		farmerId: '',
		registeredFarmAcres: 0,
		geofencedFarmAcres: 0,
		approvedGeofenceAcres: 0,
		pendingGeofenceAcres: 0,
		rejectedGeofenceAcres: 0,
		referralPhoneNo: '',
		programName: '',
	},
	selectedPoint: undefined,
	selectedPolygonIndex: undefined,
	selectedPolygonValidationStatus: 'VALID',
	selectedPolygonValidationResponse: [],
};

export const awdViewSlice = createSlice({
	name: 'awdView',
	initialState: initialState,
	reducers: {
		attachEnclosingPolygonIndexToSelectedPoint: (
			state,
			action: PayloadAction<number>,
		) => {
			state.pointsOfFarmId[state.selectedPoint!].enclosingPolygonIndex =
				action.payload;
		},
		addPolygonAreaToTotal: (state, action: PayloadAction<number>) => {
			state.selectedFarmData.geofencedFarmAcres += action.payload;
		},
		addNewFilteredPolygon: (
			state,
			action: PayloadAction<EditablePolygon>,
		) => {
			// TODO: try to improve this ⬇
			let placeIdExists = false;
			// eslint-disable-next-line no-restricted-syntax
			for (const el of Object.values(state.editablePolygons)) {
				// checking if polygon with given placeId is already inserted
				if (el.placeId === action.payload.placeId) {
					placeIdExists = true;
					break;
				}
			}
			if (!placeIdExists) {
				state.editablePolygons[
					Object.keys(state.editablePolygons).length
				] = action.payload;
			}
		},
		calculateCurrentFarmGeofencedArea: (state) => {
			let totalArea = 0;
			Object.values(state.editablePolygons).forEach((el: any) => {
				totalArea += window.google.maps.geometry.spherical.computeArea(
					el.geometry,
				);
			});
			state.selectedFarmData.geofencedFarmAcres =
				totalArea * AREA_CONVERSION_FACTOR;
		},
		calculateApprovedGeofenceAcres: (state) => {
			let totalArea = 0;
			Object.values(state.editablePolygons).forEach((el: any) => {
				if (el.polygonApprovalStatus === 'GEOFENCING_SAVED')
					totalArea += google.maps.geometry.spherical.computeArea(
						el.geometry,
					);
			});
			state.selectedFarmData.approvedGeofenceAcres =
				totalArea * AREA_CONVERSION_FACTOR;
		},
		calculatePendingGeofenceAcres: (state) => {
			let totalArea = 0;
			Object.values(state.editablePolygons).forEach((el: any) => {
				if (el.polygonApprovalStatus === 'GEOFENCING_PENDING')
					totalArea += google.maps.geometry.spherical.computeArea(
						el.geometry,
					);
			});
			state.selectedFarmData.pendingGeofenceAcres =
				totalArea * AREA_CONVERSION_FACTOR;
		},
		calculateRejectedGeofenceAcres: (state) => {
			let totalArea = 0;
			Object.values(state.editablePolygons).forEach((el: any) => {
				if (el.polygonApprovalStatus === 'GEOFENCING_REJECTED')
					totalArea += google.maps.geometry.spherical.computeArea(
						el.geometry,
					);
			});
			state.selectedFarmData.rejectedGeofenceAcres =
				totalArea * AREA_CONVERSION_FACTOR;
		},
		// decrementGeofencedPlots: (state) => {
		// 	state.selectedFarmData = {
		// 		...state.selectedFarmData,
		// 		geofencedPlots: state.selectedFarmData.geofencedPlots - 1,
		// 	};
		// },
		deductPolygonAreaFromTotal: (state, action: PayloadAction<number>) => {
			state.selectedFarmData.geofencedFarmAcres -= action.payload;
		},
		detachDeletedPolygonIndexFromEnclosedPoints: (
			state,
			action: PayloadAction<number>,
		) => {
			let pointsOfFarmIdKeysList = Object.keys(state.pointsOfFarmId);
			pointsOfFarmIdKeysList.forEach((pointIdentifier) => {
				if (
					state.pointsOfFarmId[pointIdentifier]
						.enclosingPolygonIndex === action.payload
				) {
					state.pointsOfFarmId[
						pointIdentifier
					].enclosingPolygonIndex = undefined;
				}
			});
		},
		// incrementGeofencedPlots: (state) => {
		// 	state.selectedFarmData = {
		// 		...state.selectedFarmData,
		// 		geofencedPlots: state.selectedFarmData.geofencedPlots + 1,
		// 	};
		// },
		insertNewEditablePolygon: (
			state,
			action: PayloadAction<EditablePolygon>,
		) => {
			const totalEditablePolygons = Object.keys(
				state.editablePolygons,
			).length;
			state.editablePolygons = {
				...state.editablePolygons,
				[totalEditablePolygons]: action.payload,
			};
		},
		resetEditablePolygons: (state) => {
			state.editablePolygons = {};
		},
		resetPointMapTiles: (state) => {
			state.pointMapTiles = {};
		},
		resetPolygonMapTiles: (state) => {
			state.polygonMapTiles = {};
		},
		resetPolygonDrawingStage: (state) => {
			state.polygonDrawingStage = 'PRE_START';
		},
		resetAWDStore: () => initialState,
		selectLastEditablePolygon: (state) => {
			const totalEditablePolygons = Object.keys(
				state.editablePolygons,
			).length;
			state.selectedPolygonIndex = totalEditablePolygons - 1;
		},
		setCurrentPolygonToApproved: (state) => {
			if (state.selectedPolygonIndex === undefined) {
				return;
			}
			let currentPolygon =
				state.editablePolygons[state.selectedPolygonIndex];
			currentPolygon.polygonApprovalStatus = 'GEOFENCING_SAVED';
		},
		setCurrentPolygonToPending: (state) => {
			if (state.selectedPolygonIndex === undefined) {
				return;
			}
			let currentPolygon =
				state.editablePolygons[state.selectedPolygonIndex];
			currentPolygon.polygonApprovalStatus = 'GEOFENCING_PENDING';
		},
		setCurrentPolygonToRejected: (state) => {
			if (state.selectedPolygonIndex === undefined) {
				return;
			}
			let currentPolygon =
				state.editablePolygons[state.selectedPolygonIndex];
			currentPolygon.polygonApprovalStatus = 'GEOFENCING_REJECTED';
		},
		setEditablePolygons: (
			state,
			action: PayloadAction<Record<string, EditablePolygon>>,
		) => {
			state.editablePolygons = { ...action.payload };
		},
		setIsDrawEnabled: (state, action: PayloadAction<boolean>) => {
			state.isDrawEnabled = action.payload;
		},
		setIsSelectedPolygonBlocked: (
			state,
			action: PayloadAction<PolygonBlockedStatus>,
		) => {
			state.isSelectedPolygonBlocked = action.payload === 'BLOCKED';
		},
		setIsSelectedPolygonEditable: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.isSelectedPolygonEditable = action.payload;
		},
		setIsSelectedPolygonSaved: (state, action: PayloadAction<boolean>) => {
			state.isSelectedPolygonSaved = action.payload;
		},
		setPointsOfFarmId: (
			state,
			action: PayloadAction<Record<string, PointMetaData>>,
		) => {
			state.pointsOfFarmId = {
				...state.pointsOfFarmId,
				...action.payload,
			};
		},
		setPointMapTiles: (
			state,
			action: PayloadAction<Record<string, PointData>>,
		) => {
			state.pointMapTiles = {
				...state.pointMapTiles,
				...action.payload,
			};
		},
		setPolygonMapTiles: (
			state,
			action: PayloadAction<Record<string, PolygonData>>,
		) => {
			state.polygonMapTiles = {
				...state.polygonMapTiles,
				...action.payload,
			};
		},
		setSelectedFarmData: (state, action) => {
			state.selectedFarmData = {
				...state.selectedFarmData,
				...action.payload,
			};
		},
		setSelectedPoint: (state, action: PayloadAction<string>) => {
			state.selectedPoint = action.payload;
		},
		setSelectedPolygonIndex: (
			state,
			action: PayloadAction<undefined | number>,
		) => {
			if (action.payload !== undefined) {
				state.isSelectedPolygonBlocked =
					state.editablePolygons[action.payload].isPolygonLocked ===
					'BLOCKED';
			} else {
				state.isSelectedPolygonBlocked = false;
			}
			state.selectedPolygonIndex = action.payload;
		},
		setSelectedPolygonValidationStatus: (
			state,
			action: PayloadAction<PolygonValidationStatus>,
		) => {
			state.selectedPolygonValidationStatus = action.payload;
		},
		setSelectedPolygonValidationResponse: (
			state,
			action: PayloadAction<ValidationResponse[]>,
		) => {
			state.selectedPolygonValidationResponse = action.payload;
		},
		updatePolygonDrawingStage: (
			state,
			action: PayloadAction<PolygonDrawingStage>,
		) => {
			state.polygonDrawingStage = action.payload;
		},
		updatePointData: (
			state,
			action: PayloadAction<Record<string, any>>,
		) => {
			const { pointIdentifier, payload } = action.payload;
			if (!state.pointsOfFarmId[pointIdentifier]) {
				return;
			}
			let temp = {
				[pointIdentifier]: {
					...state.pointsOfFarmId[pointIdentifier],
					...payload,
				},
			};
			state.pointsOfFarmId = { ...state.pointsOfFarmId, ...temp };
		},
		updatePointPlaceTag: (
			state,
			action: PayloadAction<{
				pointIdentifier: string;
				tagName: string;
				tagPropertyKey: string;
				value: string | number | boolean;
				valueType: string;
			}>,
		) => {
			const {
				pointIdentifier,
				tagName,
				tagPropertyKey,
				value,
				valueType,
			} = action.payload;
			if (!state.pointsOfFarmId[pointIdentifier]) {
				return;
			}
			let tempPoint = JSON.parse(
				JSON.stringify(state.pointsOfFarmId[pointIdentifier]),
			);
			for (let i = 0; i < tempPoint.placeTags.length; i++) {
				if (
					tempPoint.placeTags[i].TagName === tagName &&
					tempPoint.placeTags[i].TagPropertyKey === tagPropertyKey
				) {
					tempPoint.placeTags[i] = {
						...tempPoint.placeTags[i],
						TagName: tagName,
						TagPropertyKey: tagPropertyKey,
						value: {
							valueType,
							stringValues: [],
							int64Values: [],
							int32Values: [],
							floatValues: [],
							boolValues: [],
							...tempPoint.placeTags[i].value,
						},
					};
					switch (valueType) {
						case 'VALUE_TYPE_STRING':
							tempPoint.placeTags[i].value.stringValues[0] =
								value;
							break;
						case 'VALUE_TYPE_INT64':
							tempPoint.placeTags[i].value.int64Values[0] = value;
							break;
						case 'VALUE_TYPE_INT32':
							tempPoint.placeTags[i].value.int32Values[0] = value;
							break;
						case 'VALUE_TYPE_DOUBLE':
							tempPoint.placeTags[i].value.floatValues[0] = value;
							break;
						case 'VALUE_TYPE_BOOLEAN':
							tempPoint.placeTags[i].value.boolValues[0] = value;
							break;
						default:
							tempPoint.placeTags[i].value.stringValues[0] =
								value;
					}
					break;
				}
			}
			state.pointsOfFarmId[pointIdentifier] = tempPoint;
		},
		updateSelectedPolygonRejectionReasons: (
			state,
			action: PayloadAction<string[]>,
		) => {
			if (state.selectedPolygonIndex !== undefined) {
				state.editablePolygons[
					state.selectedPolygonIndex
				].rejectionReasons = action.payload;
			}
		},
	},
});

export const {
	attachEnclosingPolygonIndexToSelectedPoint,
	addPolygonAreaToTotal,
	addNewFilteredPolygon,
	calculateCurrentFarmGeofencedArea,
	calculateApprovedGeofenceAcres,
	calculatePendingGeofenceAcres,
	calculateRejectedGeofenceAcres,
	// decrementGeofencedPlots,
	deductPolygonAreaFromTotal,
	detachDeletedPolygonIndexFromEnclosedPoints,
	// incrementGeofencedPlots,
	insertNewEditablePolygon,
	resetEditablePolygons,
	resetPointMapTiles,
	resetPolygonMapTiles,
	resetPolygonDrawingStage,
	resetAWDStore,
	selectLastEditablePolygon,
	setCurrentPolygonToApproved,
	setCurrentPolygonToPending,
	setCurrentPolygonToRejected,
	setEditablePolygons,
	setIsDrawEnabled,
	setIsSelectedPolygonBlocked,
	setIsSelectedPolygonEditable,
	setIsSelectedPolygonSaved,
	setPointsOfFarmId,
	setPointMapTiles,
	setPolygonMapTiles,
	setSelectedFarmData,
	setSelectedPoint,
	setSelectedPolygonIndex,
	setSelectedPolygonValidationStatus,
	setSelectedPolygonValidationResponse,
	updatePointData,
	updatePolygonDrawingStage,
	updatePointPlaceTag,
	updateSelectedPolygonRejectionReasons,
} = awdViewSlice.actions;

export default awdViewSlice.reducer;
